import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { itIncidentsAPI, usersAPI, ITIncidentSummaryStats } from '../services/api';
import { ITIncident, ITIncidentCategory, ITIncidentPriority, ITIncidentStatus, User } from '../types';

const categoryOptions: { label: string; value: ITIncidentCategory | 'all' }[] = [
  { label: 'Sve', value: 'all' },
  { label: 'Hardware', value: 'hardware' },
  { label: 'Software', value: 'software' },
  { label: 'Mreža', value: 'network' },
  { label: 'Bezbednost', value: 'security' },
  { label: 'Greška korisnika', value: 'user_error' },
  { label: 'Ostalo', value: 'other' }
];

const priorityOptions: { label: string; value: ITIncidentPriority | 'all' }[] = [
  { label: 'Svi', value: 'all' },
  { label: 'Nizak', value: 'low' },
  { label: 'Srednji', value: 'medium' },
  { label: 'Visok', value: 'high' },
  { label: 'Kritičan', value: 'critical' }
];

const statusOptions: { label: string; value: ITIncidentStatus | 'all' }[] = [
  { label: 'Svi', value: 'all' },
  { label: 'Otvoren', value: 'open' },
  { label: 'U toku', value: 'in_progress' },
  { label: 'Rešen', value: 'resolved' },
  { label: 'Zatvoren', value: 'closed' }
];

const prioritySeverity: Record<ITIncidentPriority, 'success' | 'info' | 'warning' | 'danger'> = {
  low: 'success',
  medium: 'info',
  high: 'warning',
  critical: 'danger'
};

const statusSeverity: Record<ITIncidentStatus, 'success' | 'info' | 'warning' | 'danger'> = {
  open: 'danger',
  in_progress: 'warning',
  resolved: 'info',
  closed: 'success'
};

const ITIncidents: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [incidents, setIncidents] = useState<ITIncident[]>([]);
  const [summary, setSummary] = useState<ITIncidentSummaryStats | null>(null);
  const [users, setUsers] = useState<User[]>([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ITIncidentCategory | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<ITIncidentPriority | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<ITIncidentStatus | 'all'>('all');

  const [editVisible, setEditVisible] = useState(false);
  const [editing, setEditing] = useState<ITIncident | null>(null);
  const [form, setForm] = useState<{
    title: string;
    description: string;
    category: ITIncidentCategory;
    priority: ITIncidentPriority;
    affectedUser: string;
    affectedSystem: string;
    location: string;
    assignedTo: string | null;
  }>({
    title: '',
    description: '',
    category: 'other',
    priority: 'medium',
    affectedUser: '',
    affectedSystem: '',
    location: '',
    assignedTo: null
  });

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [incData, sumData, userData] = await Promise.all([
        itIncidentsAPI.getAll(),
        itIncidentsAPI.getSummary(),
        usersAPI.getAll()
      ]);
      setIncidents(incData);
      setSummary(sumData);
      setUsers(userData);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      setError(e?.response?.data?.error || 'Greška pri učitavanju IT incidenata');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({
      title: '',
      description: '',
      category: 'other',
      priority: 'medium',
      affectedUser: '',
      affectedSystem: '',
      location: '',
      assignedTo: null
    });
    setEditVisible(true);
  };

  const openEdit = (incident: ITIncident) => {
    setEditing(incident);
    setForm({
      title: incident.title,
      description: incident.description,
      category: incident.category,
      priority: incident.priority,
      affectedUser: incident.affectedUser || '',
      affectedSystem: incident.affectedSystem || '',
      location: incident.location || '',
      assignedTo:
        (typeof incident.assignedTo === 'object' && incident.assignedTo?._id) || null
    });
    setEditVisible(true);
  };

  const canSubmit = useMemo(
    () => form.title.trim() && form.description.trim(),
    [form.title, form.description]
  );

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Validacija',
        detail: 'Naslov i opis su obavezni',
        life: 3000
      });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        priority: form.priority,
        affectedUser: form.affectedUser || undefined,
        affectedSystem: form.affectedSystem || undefined,
        location: form.location || undefined,
        assignedTo: form.assignedTo || undefined
      };
      if (editing) {
        await itIncidentsAPI.update(editing._id, payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Uspešno',
          detail: 'Incident ažuriran',
          life: 2500
        });
      } else {
        await itIncidentsAPI.create(payload);
        toast.current?.show({
          severity: 'success',
          summary: 'Uspešno',
          detail: 'Incident kreiran',
          life: 2500
        });
      }
      setEditVisible(false);
      await load();
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.current?.show({
        severity: 'error',
        summary: 'Greška',
        detail: e?.response?.data?.error || 'Greška pri čuvanju incidenta',
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredIncidents = incidents.filter((i) => {
    const searchMatch =
      !search ||
      i.title.toLowerCase().includes(search.toLowerCase()) ||
      i.description.toLowerCase().includes(search.toLowerCase()) ||
      i.reportedByName.toLowerCase().includes(search.toLowerCase()) ||
      (i.assignedToName || '').toLowerCase().includes(search.toLowerCase());

    const categoryMatch =
      categoryFilter === 'all' || i.category === categoryFilter;
    const priorityMatch =
      priorityFilter === 'all' || i.priority === priorityFilter;
    const statusMatch = statusFilter === 'all' || i.status === statusFilter;

    return searchMatch && categoryMatch && priorityMatch && statusMatch;
  });

  const priorityBody = (row: ITIncident) => (
    <Tag
      value={
        row.priority === 'low'
          ? 'Nizak'
          : row.priority === 'medium'
          ? 'Srednji'
          : row.priority === 'high'
          ? 'Visok'
          : 'Kritičan'
      }
      severity={prioritySeverity[row.priority]}
    />
  );

  const statusBody = (row: ITIncident) => (
    <Tag
      value={
        row.status === 'open'
          ? 'Otvoren'
          : row.status === 'in_progress'
          ? 'U toku'
          : row.status === 'resolved'
          ? 'Rešen'
          : 'Zatvoren'
      }
      severity={statusSeverity[row.status]}
    />
  );

  const categoryBody = (row: ITIncident) => {
    switch (row.category) {
      case 'hardware':
        return 'Hardware';
      case 'software':
        return 'Software';
      case 'network':
        return 'Mreža';
      case 'security':
        return 'Bezbednost';
      case 'user_error':
        return 'Greška korisnika';
      default:
        return 'Ostalo';
    }
  };

  const dateBody = (row: ITIncident) =>
    new Date(row.createdAt).toLocaleString('sr-RS');

  const assigneeOptions = useMemo(
    () =>
      users
        .filter((u) => u.role === 'admin' || u.role === 'direktor')
        .map((u) => ({
          label: `${u.firstName} ${u.lastName}`,
          value: u._id
        })),
    [users]
  );

  return (
    <div className="p-4">
      <Toast ref={toast} />

      {/* Cards summary */}
      <div className="grid mb-4">
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-600 mb-1">Ukupno</div>
            <div className="text-4xl font-bold">
              {summary?.total ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-600 mb-1">Otvoreni</div>
            <div className="text-4xl font-bold">
              {summary?.byStatus.open ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-600 mb-1">U toku</div>
            <div className="text-4xl font-bold">
              {summary?.byStatus.in_progress ?? 0}
            </div>
          </Card>
        </div>
        <div className="col-12 md:col-3">
          <Card>
            <div className="text-600 mb-1">Rešeni</div>
            <div className="text-4xl font-bold">
              {summary?.byStatus.resolved ?? 0}
            </div>
          </Card>
        </div>
      </div>

      <Card>
        <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <h2 className="m-0">IT Incidenti</h2>
          <div className="flex gap-2 flex-wrap">
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži..."
            />
            <Button
              label="Novi incident"
              icon="pi pi-plus"
              onClick={openNew}
            />
          </div>
        </div>

        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="grid mb-3">
          <div className="col-12 md:col-4">
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži po naslovu"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <Dropdown
              value={categoryFilter}
              options={categoryOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setCategoryFilter(e.value)}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-3">
            <Dropdown
              value={priorityFilter}
              options={priorityOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setPriorityFilter(e.value)}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
          <div className="col-12 md:col-2">
            <Dropdown
              value={statusFilter}
              options={statusOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setStatusFilter(e.value)}
              appendTo="self"
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <DataTable
          value={filteredIncidents}
          loading={loading}
          paginator
          rows={20}
          emptyMessage="Nema incidenata"
          className="p-datatable-sm"
        >
          <Column field="title" header="Naslov" sortable />
          <Column header="Kategorija" body={categoryBody} sortable />
          <Column header="Prioritet" body={priorityBody} sortable />
          <Column header="Status" body={statusBody} sortable />
          <Column
            field="reportedByName"
            header="Prijavio"
            sortable
          />
          <Column
            field="assignedToName"
            header="Dodeljen"
            sortable
          />
          <Column header="Datum" body={dateBody} sortable sortField="createdAt" />
          <Column
            header="Akcije"
            body={(row: ITIncident) => (
              <Button
                icon="pi pi-pencil"
                className="p-button-text p-button-sm"
                onClick={() => openEdit(row)}
                tooltip="Uredi"
              />
            )}
            style={{ width: '120px' }}
          />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni incident' : 'Novi incident'}
        visible={editVisible}
        onHide={() => setEditVisible(false)}
        style={{ width: '800px' }}
        breakpoints={{ '960px': '95vw', '640px': '98vw' }}
        modal
        draggable={false}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              label="Otkaži"
              className="p-button-secondary p-button-outlined"
              onClick={() => setEditVisible(false)}
            />
            <Button
              label="Sačuvaj"
              icon="pi pi-check"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={loading}
            />
          </div>
        }
      >
        <div className="p-fluid formgrid grid">
          <div className="field col-12">
            <label>Naslov *</label>
            <InputText
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            />
          </div>
          <div className="field col-12">
            <label>Opis *</label>
            <InputTextarea
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={5}
              autoResize
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Kategorija *</label>
            <Dropdown
              value={form.category}
              options={categoryOptions.filter((c) => c.value !== 'all')}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, category: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Prioritet *</label>
            <Dropdown
              value={form.priority}
              options={priorityOptions.filter((p) => p.value !== 'all')}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, priority: e.value }))}
              appendTo="self"
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Pogođeni korisnik</label>
            <InputText
              value={form.affectedUser}
              onChange={(e) =>
                setForm((p) => ({ ...p, affectedUser: e.target.value }))
              }
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Pogođeni sistem</label>
            <InputText
              value={form.affectedSystem}
              onChange={(e) =>
                setForm((p) => ({ ...p, affectedSystem: e.target.value }))
              }
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Lokacija</label>
            <InputText
              value={form.location}
              onChange={(e) =>
                setForm((p) => ({ ...p, location: e.target.value }))
              }
            />
          </div>
          <div className="field col-12 md:col-6">
            <label>Dodeli (Admin)</label>
            <Dropdown
              value={form.assignedTo}
              options={assigneeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(e) => setForm((p) => ({ ...p, assignedTo: e.value }))}
              placeholder="Izaberite admina"
              filter
              showClear
              appendTo="self"
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ITIncidents;


